import React, { useState, useEffect, useReducer, useContext } from "react";
import { getSocket } from "../../services/socket-io";
import api from "../../services/api";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import ContactWithoutTicketItem from "../ContactWithoutTicketItem";
import ListSubheader from "@material-ui/core/ListSubheader";

import useTickets from "../../hooks/useTickets";
import useResyncOnResume from "../../hooks/useResyncOnResume";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_IF_EXISTS") {
		// Like UPDATE_TICKET but never adds — only updates if already in list.
		// Used for status=null (search) lists to avoid duplicating tickets
		// already managed by status-specific lists.
		const ticket = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		}
		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_AND_BUMP") {
		// Like UPDATE_TICKET but also moves the ticket to the top of the list.
		// Used by the scheduled-message flow: programar un envío bumpea el chat sin
		// alterar lastMessage ni unread counts.
		const ticket = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			// Preserve in-memory fields that the bumped payload may not carry
			// (lastMessage stays intact — backend deliberately does not touch it).
			state[ticketIndex] = { ...state[ticketIndex], ...ticket };
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else {
			state.unshift(ticket);
		}
		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "TOUCH_TICKET") {
		// Crea nueva referencia del ticket para forzar re-render sin cambiar sus datos.
		// Usado por el socket ticketPending para que TicketListItem re-fetchee el pending.
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex] = { ...state[ticketIndex], _pendingUpdate: Date.now() };
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const { status, socketStatus, searchParam, showAll, showOrphans, selectedQueueIds, updateCount, style, hideChannels, showGroups, onAcceptTicket, contactStateId, tagIds, contactTypeIds, filterUserIds, includeUnassigned, queueAssignment, sortOrder, onlyUnanswered, showStatusBadge, onStartChatWithContact,
			selectionMode, selectedIds, onToggleSelect, onLongPress, onEnterSelection, onVisibleTicketsChange,
			deletedView, onRestoreSingle, onHardDeleteSingle, isAdmin } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [tdrObjective, setTdrObjective] = useState(10);
	// feat_273: sesiones de turnos vivas → { ticketId: expiresAt } para el badge
	// del zócalo. GET active al montar + refresco por socket (sin polling).
	const [turnosSessions, setTurnosSessions] = useState({});
	// feat_346: re-sync al despertar. Bumpear esta clave = RESET + re-fetch de la
	// página 1. Va en las deps del effect de RESET *y* en las de useTickets:
	// setPageNumber(1) solo NO alcanza (si ya vale 1 no cambia nada y el GET no
	// se re-dispara), y un re-fetch sin RESET rompe el orden (LOAD_TICKETS
	// fusiona y pushea los nuevos al final).
	const [resyncKey, setResyncKey] = useState(0);
	useResyncOnResume(() => setResyncKey(k => k + 1));
	const { user } = useContext(AuthContext);

	// T4: cargar tdrObjectiveMinutes desde settings una sola vez
	useEffect(() => {
		api.get("/settings").then(({ data }) => {
			const setting = data.find(s => s.key === "tdrObjectiveMinutes");
			if (setting) setTdrObjective(parseInt(setting.value) || 10);
		}).catch(() => {});
	}, []);

	// feat_301: se serializa acá (igual que queueIds) para que el array NO sea
	// dep inestable de los effects — un array nuevo por render dispararía
	// RESET + refetch en loop.
	const filterUserIdsJson = JSON.stringify(filterUserIds || []);

	// feat_323: `status` puede ser array (bandeja unificada, ["open","pending"]).
	// Misma trampa de referencia que filterUserIds → primitiva estable para las
	// deps. El valor original se sigue pasando a useTickets y al render.
	// feat_325: `queueAssignment` se suma a la misma clave — el handler del
	// socket lo lee para decidir si una fila sigue perteneciendo a esta lista,
	// así que si no está en las deps el guard se queda con el valor viejo.
	// feat_326: `sortOrder` y `onlyUnanswered` van a la misma clave. El orden
	// cambia el fetch; el filtro además gobierna el guard `answeredMismatch` del
	// socket, que sin esta dep leería el valor del render en que se suscribió.
	const filterKey = [
		Array.isArray(status) ? status.slice().sort().join(",") : (status || ""),
		queueAssignment || "any",
		sortOrder || "chrono",
		onlyUnanswered ? "unanswered" : "all"
	].join("|");

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterKey, searchParam, dispatch, showAll, showOrphans, selectedQueueIds, hideChannels, contactStateId, tagIds, contactTypeIds, filterUserIdsJson, includeUnassigned, resyncKey]);

	const { tickets, hasMore, loading, contactsWithoutTicket } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		showOrphans,
		queueIds: JSON.stringify(selectedQueueIds),
		hideChannels,
		contactStateId,
		tagIds: tagIds || [],
		contactTypeIds: contactTypeIds || [],
		filterUserIds: filterUserIdsJson,
		includeUnassigned: includeUnassigned ? "true" : "false",
		queueAssignment,
		sortOrder,
		onlyUnanswered,
		deleted: deletedView ? "true" : undefined,
		resyncKey,
	});

	useEffect(() => {
		// El guard original evita duplicar tickets entre listas. Tab Eliminados
		// (deletedView=true) tiene su propia instancia con key={deleted-<n>} →
		// state aislado, sin colisión. Sin esta excepción, los soft-deleted nunca
		// se cargan al reducer y la tab queda vacía aunque el backend los devuelva.
		if (!status && !searchParam && !contactStateId && !deletedView) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = getSocket();

		const shouldUpdateTicket = ticket => {
			if (searchParam) return false;
			// feat_301: con el filtro por usuario activo, el push en vivo respeta
			// el mismo recorte que el fetch HTTP (si no, entrarían tickets de
			// asesores que el admin sacó del filtro).
			if (filterUserIds?.length > 0 || includeUnassigned) {
				const matchesUser = (filterUserIds || []).includes(ticket.userId);
				const matchesUnassigned = includeUnassigned && !ticket.userId;
				if (!matchesUser && !matchesUnassigned) return false;
			}
			// Admin "Todos" ON: show everything
			if (showAll) return true;
			// Admin "Todos" OFF: respects queueIds + showOrphans
			if (user?.profile === "admin") {
				if (!ticket.queueId) return !!showOrphans;
				return selectedQueueIds.indexOf(ticket.queueId) > -1;
			}
			// Non-admin: directly assigned OR in my queues
			if (ticket.userId === user?.id) return true;
			if (ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) > -1) return true;
			// feat_325 — lista de huérfanos: un ticket SIN cola no matchea ninguna
			// de las dos reglas de arriba, pero el asesor con `canSeeUnassigned` SÍ
			// lo recibe por HTTP (su scope incluye `userId:null` + `queueId:null`).
			// Sin esta rama el push en vivo no le llega y EN ESPERA solo se puebla
			// al refrescar. No amplía nada: el backend sigue siendo quien decide
			// qué devuelve, y ShowTicketService gatea la apertura.
			if (
				queueAssignment === "unassigned" &&
				!ticket.queueId &&
				user?.canSeeUnassigned
			) {
				return true;
			}
			return false;
		};

		const notBelongsToUserQueues = ticket =>
			selectedQueueIds.length > 0 &&
			ticket.queueId &&
			selectedQueueIds.indexOf(ticket.queueId) === -1;

		// `socketStatus` desacopla la sala de socket del `status` del fetch HTTP.
		// La papelera lo necesita: su GET /tickets va con deleted=true y SIN
		// status (mandar status="deleted" filtraría por un status inexistente y
		// devolvería la lista vacía), pero su sala sí es `deleted-${companyId}`.
		// El resto de las listas no pasa el prop → sala = status, igual que antes.
		// El companyId ya NO se manda: el backend lo toma del JWT (fix cross-tenant).
		//
		// feat_323 — bandeja unificada: `status` puede ser un array, y entonces la
		// lista tiene que unirse a UNA SALA POR STATUS. Con una sola sala el push
		// en vivo del otro status no llegaría nunca (un ticket nuevo `pending` no
		// aparecería hasta refrescar). El backend ya lo soporta sin cambios: su
		// allowlist (ALLOWED_TICKET_ROOMS, feat_320) valida cada emit por separado.
		const joinRoom = () => {
			const room = socketStatus || status;
			if (!room) {
				socket.emit("joinNotification");
			} else if (Array.isArray(room)) {
				room.forEach(r => socket.emit("joinTickets", r));
			} else {
				socket.emit("joinTickets", room);
			}
		};

		socket.on("connect", joinRoom);
		if (socket.connected) joinRoom();

		const handleTicket = data => {
			const _t = data.ticket;

			if (data.action === "updateUnread") {
				dispatch({
					type: "RESET_UNREAD",
					payload: data.ticketId,
				});
			}

			if (data.action === "update" || data.action === "updateAndBump") {
				// If this list has a defined status and the ticket's status no longer
				// matches, remove it immediately (handles close and transfer cases)
				//
				// feat_323 — con `status` array (bandeja unificada) un `!==` sería
				// SIEMPRE true y borraría toda fila que llegue por socket: la lista
				// se vaciaría sola. El criterio pasa a ser pertenencia al conjunto.
				const statusMismatch = Array.isArray(status)
					? !status.includes(data.ticket.status)
					: !!status && data.ticket.status !== status;
				// feat_325 — el status NO alcanza en modo smart. Las dos listas se
				// unen a la MISMA sala `pending-<cid>` (el socket del navegador es
				// singleton multiplexado), así que cuando a un huérfano le asignan
				// cola el evento llega a las dos con el status SIN cambiar: la de
				// huérfanos no lo sacaría y la de asignados lo agregaría → la misma
				// fila en las dos listas hasta refrescar. El bucket se decide por la
				// presencia de cola, igual que el WHERE del fetch.
				const queueMismatch =
					(queueAssignment === "unassigned" && data.ticket.queueId != null) ||
					(queueAssignment === "assigned" && data.ticket.queueId == null) ||
					(queueAssignment === "assigned_or_open" &&
						data.ticket.queueId == null &&
						data.ticket.status !== "open");
				// feat_326 — mismo razonamiento que queueMismatch, pero por el
				// criterio del filtro "sin responder": si el asesor contesta, el
				// ticket deja de pertenecer a esta lista aunque su status y su
				// cola no hayan cambiado. `lastMessageSenderType` viaja en el
				// payload del socket (ShowTicketService lo devuelve).
				const answeredMismatch =
					!!onlyUnanswered && data.ticket.lastMessageSenderType !== "customer";
				// feat_327 — el guard de ALTA (`shouldUpdateTicket`, feat_301) ya
				// filtraba por usuario, pero el de BAJA no: devolver `false` ahí
				// significa "no hacer nada", NO "sacarlo". Un ticket reasignado a
				// otro asesor (o desasignado) con el status, la cola y el
				// `lastMessageSenderType` SIN cambiar quedaba colgado en la lista
				// filtrada hasta recargar. Misma clase que queueMismatch (feat_325)
				// y answeredMismatch (feat_326).
				// Replica el OR del backend (ListTicketsService feat_301):
				//   userId IN ids  OR  (includeUnassigned AND userId IS NULL)
				// ⚠ Es un OR, no un AND: con `includeUnassigned` prendido los
				// tickets sin dueño se QUEDAN aunque haya `filterUserIds`.
				// Gateado por `userFilterActive` → filtro apagado = false = cero
				// regresión. `userId` viaja siempre en el payload (ShowTicketService
				// no restringe attributes del Ticket) y es `null` para sin asignar.
				const userFilterActive =
					filterUserIds?.length > 0 || !!includeUnassigned;
				const userMismatch =
					userFilterActive &&
					!(filterUserIds || []).includes(Number(data.ticket.userId)) &&
					!(includeUnassigned && !data.ticket.userId);
				if (statusMismatch || queueMismatch || answeredMismatch || userMismatch) {
					dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
				} else if (shouldUpdateTicket(data.ticket)) {
					dispatch({
						// updateAndBump → move-to-top reducer; preserves lastMessage and
						// unread counts (backend does not touch them when bumping).
						// status=null lists (search mode) must not auto-add tickets that
						// are already managed by a status-specific list — only update
						// tickets already present in this list.
						type:
							data.action === "updateAndBump"
								? "UPDATE_TICKET_AND_BUMP"
								: status
								? "UPDATE_TICKET"
								: "UPDATE_TICKET_IF_EXISTS",
						payload: data.ticket,
					});
				}
			}

			if (
				(data.action === "update" || data.action === "updateAndBump") &&
				notBelongsToUserQueues(data.ticket)
			) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		};

		const handleAppMessage = data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
				});
			}
		};

		const handleContact = data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		};

		// Escuchar eventos de ticketPending para actualizar badge en tiempo real
		const handleTicketPending = data => {
			if (data.ticketId) {
				dispatch({ type: "TOUCH_TICKET", payload: data.ticketId });
			}
		};

		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);
		socket.on("contact", handleContact);
		socket.on("ticketPending", handleTicketPending);

		return () => {
			// SINGLETON getSocket(): jamás socket.disconnect() — mataría la conexión
			// de TODA la app. Solo off() selectivo de los listeners de este componente.
			socket.off("connect", joinRoom);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
			socket.off("contact", handleContact);
			socket.off("ticketPending", handleTicketPending);
		};
	// Use stable primitives (id, companyId) instead of the whole user object
	// to avoid re-running this effect on every user state update that creates
	// a new reference — which would disconnect/reconnect the socket unnecessarily.
	// feat_323/325: filterKey por el mismo motivo (un array re-suscribiría el socket
	// en cada render).
	}, [filterKey, socketStatus, searchParam, showAll, showOrphans, user?.id, user?.companyId, selectedQueueIds, filterUserIdsJson, includeUnassigned]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
    if (typeof updateCount === "function") {
      // feat_323 — 2º argumento OPCIONAL con el desglose por status, que la
      // bandeja unificada necesita para pintar los dos badges desde UNA lista.
      // Los callers viejos reciben `(val) => setXCount(val)` y lo ignoran →
      // comportamiento idéntico.
      //
      // ⚠ Va acá y no en `onVisibleTicketsChange`: esa prop se invoca durante
      // el RENDER del hijo, y un setState del padre desde ahí es un setState en
      // fase de render ("Cannot update a component while rendering a different
      // component"). Este useEffect es el único punto seguro.
      updateCount(ticketsList.length, {
        open: ticketsList.filter(t => t.status === "open").length,
        pending: ticketsList.filter(t => t.status === "pending").length,
        closed: ticketsList.filter(t => t.status === "closed").length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsList]);

	// feat_273: sesiones de turnos vivas → set de ticketIds para el badge del zócalo.
	useEffect(() => {
		let alive = true;
		const refresh = () => {
			api
				.get("/turnos-chat/sessions/active")
				.then(({ data }) => {
					if (!alive) return;
					const map = {};
					(data || []).forEach((s) => {
						map[s.ticketId] = s.expiresAt;
					});
					setTurnosSessions(map);
				})
				.catch(() => {});
		};
		refresh();
		const socket = getSocket();
		const onSession = () => refresh(); // open/close → refetch
		socket.on("turnosChatSession", onSession);
		return () => {
			alive = false;
			// SINGLETON: el off() selectivo alcanza. NUNCA disconnect().
			socket.off("turnosChatSession", onSession);
		};
	}, []);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{(() => {
						const visibleTickets = showGroups
							? ticketsList
							: ticketsList.filter(t => !t.isGroup);
						const visibleContactsWithoutTicket = searchParam
							? (showGroups
								? (contactsWithoutTicket || [])
								: (contactsWithoutTicket || []).filter(c => !c.isGroup))
							: [];
						if (
							visibleTickets.length === 0 &&
							visibleContactsWithoutTicket.length === 0 &&
							!loading
						) {
							return (
								<div className={classes.noTicketsDiv}>
									<span className={classes.noTicketsTitle}>
										{i18n.t("ticketsList.noTicketsTitle")}
									</span>
									<p className={classes.noTicketsText}>
										{i18n.t("ticketsList.noTicketsMessage")}
									</p>
								</div>
							);
						}
						// Notify parent of visible ticket IDs (used by "Select all visible")
						if (typeof onVisibleTicketsChange === "function") {
							onVisibleTicketsChange(visibleTickets.map(t => t.id));
						}
						const selectedSet = selectedIds || new Set();
						return (
							<>
								{visibleTickets.map(ticket => (
									<TicketListItem
										ticket={ticket}
										key={ticket.id}
										tdrObjective={tdrObjective}
										hasTurnosSession={Object.prototype.hasOwnProperty.call(turnosSessions, ticket.id)}
										turnosExpiresAt={turnosSessions[ticket.id]}
										onAcceptTicket={onAcceptTicket}
										showStatusBadge={!!showStatusBadge}
										selectionMode={!!selectionMode}
										isSelected={selectedSet.has ? selectedSet.has(ticket.id) : false}
										onToggleSelect={onToggleSelect}
										onLongPress={onLongPress}
										onEnterSelection={onEnterSelection}
										deletedView={!!deletedView}
										onRestoreSingle={onRestoreSingle}
										onHardDeleteSingle={onHardDeleteSingle}
										isAdmin={!!isAdmin}
									/>
								))}
								{visibleContactsWithoutTicket.length > 0 && (
									<>
										<ListSubheader
											disableSticky
											style={{
												background: "#f5f5f5",
												lineHeight: "32px",
												color: "rgb(67, 83, 105)",
												fontWeight: 600,
											}}
										>
											{i18n.t("ticketsList.contactsWithoutChat")} ({visibleContactsWithoutTicket.length})
										</ListSubheader>
										{visibleContactsWithoutTicket.map(contact => (
											<ContactWithoutTicketItem
												key={`contact-${contact.id}`}
												contact={contact}
												onStartChat={c => onStartChatWithContact && onStartChatWithContact(c)}
											/>
										))}
									</>
								)}
							</>
						);
					})()}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
